import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useOrdersLogic from './orders-components/OrdersLogic';
import DeleteConfirmationModal from './orders-components/DeleteConfirmationModal';
import StatusSummary from './orders-components/StatusSummary';
import OrderTable from './orders-components/OrderTable';
import OrderFilters from './orders-components/OrderFilters';
import Pagination from './orders-components/Pagination';
import OrdersHeader from './orders-components/OrdersHeader';

import { ToastContainer, toast } from 'react-toastify';
const OrdersPage = () => {
    const location = useLocation();
    
    // Add state for selected orders
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    // Add platform filter state
    const [platformFilter, setPlatformFilter] = useState('');
    
    // Add handler for order selection
    const handleOrderSelection = (orderId, status) => {
        setSelectedOrders(prevSelected => {
            const isSelected = prevSelected.some(order => order.id === orderId);
            
            if (isSelected) {
                // Remove from selection
                return prevSelected.filter(order => order.id !== orderId);
            } else {
                // Add to selection
                return [...prevSelected, { id: orderId, status }];
            }
        });
    };

    // Add a function to check if any selected order can be synchronized
    const canEnableSync = () => {
        if (!selectedOrders || selectedOrders.length === 0) return false;
        
        return selectedOrders.some(order => {
            const status = order.etat?.toLowerCase();
            return status === 'confirmed' || 
              status === 'confirmé' || 
              status === 'readytoship' || 
              status === 'ready to ship' ||
              status === 'prêt à expédier';
        });
    };

    // Add this function to your OrdersPage component
    const handleTrackOrders = (ordersToTrack) => {
        try {
            // Implement your tracking logic here
            // For example, you might call an API
            
            // Show success toast
            toast.success(`${ordersToTrack.length} commandes en cours de suivi`);
        } catch (error) {
            console.error('Error tracking orders:', error);
            toast.error("Une erreur s'est produite lors du suivi des commandes");
        }
    };
    

    const {
        // States
        loading,
        error,
        statusFilter,
        setStatusFilter,
        openDropdown,
        setOpenDropdown,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        orderToDelete,
        statusSummary,
        allowedStatusOptions,
        currentPage,
        pageSize,
        totalItems,
        apiStatusFilter,
        // Date filter states
        // Status options
        statusOptions,
        // Add sync filter state
        syncroFilter,
        setSyncroFilter,
        
        // Calculated values
        filteredOrders,
        currentOrders,
        totalPages,
        indexOfFirstOrder,
        indexOfLastOrder,

        // Functions
        formatDate,
        handleUpdateStatus,
        translateStatus,
        getClientFullName,
        paginate,
        getStatusColor,
        exportToExcel,
        toggleDropdown,
        handleDeleteOrder,
        confirmDeleteOrder,
        handleStatusFilterChange,
        applyFilters,
        fetchHeaderData,
        // Add sync function if necessary
        handleSyncOrders,
        handlePageSizeChange, // Add this line
        refreshOrderData, // Add this line
        refreshStatusSummary // Add this line
    } = useOrdersLogic();

// Track whether initial restore from sessionStorage has completed.
// We must NOT save the default `pending` to storage before restoring, otherwise
// the saved filter from previous session is overwritten on mount.
const hasRestoredRef = React.useRef(false);

// Save current page + filter to sessionStorage whenever they change (after restore)
useEffect(() => {
  if (!hasRestoredRef.current) return;
  sessionStorage.setItem('ordersPageState', JSON.stringify({
    page: currentPage,
    statusFilter: apiStatusFilter,
  }));
}, [currentPage, apiStatusFilter]);

// On navigation to this page: always restore saved filter/page, and if coming
// back from edit/view scroll to the specific order card that was clicked.
useEffect(() => {
  const saved = (() => { try { return JSON.parse(sessionStorage.getItem('ordersPageState')); } catch { return null; } })();
  const backFromDetail = location.state?.fromOrderEdit;

  // Always restore the saved filter (so confirmer / other etats persist)
  if (saved && saved.statusFilter !== undefined && saved.statusFilter !== apiStatusFilter) {
    handleStatusFilterChange(saved.statusFilter || '');
  }
  if (saved?.page && saved.page > 1 && saved.page !== currentPage) {
    setTimeout(() => paginate(saved.page), 100);
  }
  // Mark restore as complete on next tick so the save effect starts persisting
  setTimeout(() => { hasRestoredRef.current = true; }, 0);

  // Only scroll back to the specific order when returning from edit/voir
  if (backFromDetail) {
    const clickedOrderId = sessionStorage.getItem('lastClickedOrderId');
    const savedScroll = Number(sessionStorage.getItem('ordersScrollY') || 0);
    // Wait for DOM to render the order list, then scroll to the specific card/row
    const tryScroll = (attempt = 0) => {
      if (clickedOrderId) {
        const el = document.querySelector(`[data-order-id="${clickedOrderId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-[#174540]', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-[#174540]', 'ring-offset-2'), 2000);
          return;
        }
      }
      if (savedScroll > 0) {
        window.scrollTo({ top: savedScroll, behavior: 'instant' });
      }
      // Retry a few times in case orders are still loading
      if (attempt < 10) setTimeout(() => tryScroll(attempt + 1), 150);
    };
    setTimeout(() => tryScroll(), 250);
    // Clear flags after use
    window.history.replaceState({}, document.title);
    setTimeout(() => {
      sessionStorage.removeItem('lastClickedOrderId');
      sessionStorage.removeItem('ordersScrollY');
    }, 3000);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [location.key]);

useEffect(() => {
  if (location.state?.fromNotification) {
    const phoneNumber = location.state.filterPhoneNumber || '';
    const startDate = location.state.filterStartDate || '';
    const endDate = location.state.filterEndDate || '';

    setSearchTerm(phoneNumber);
    setStartDate(startDate);
    setEndDate(endDate);

    applyFilters(
      phoneNumber,
      statusFilter,
      startDate,
      endDate,
      syncroFilter
    );

    toast.info(`Filtres appliqués pour le numéro ${phoneNumber}${startDate && endDate ? ` du ${startDate} au ${endDate}` : ''}`);

    // Remove notification state so effect doesn't re-run
    window.history.replaceState({}, document.title);
  }
  // Only depend on location.state!
}, [location.state]);
  
    return (
        <div className="py-6 px-4 sm:px-6 lg:px-8">
            {/* Status Summary Cards */}
            <StatusSummary
                statusSummary={statusSummary}
                onStatusFilterChange={handleStatusFilterChange}
                activeStatus={apiStatusFilter}
            />

            {/* Header with Title and Export Button */}
            <OrdersHeader
                exportToExcel={exportToExcel}
                filteredOrders={filteredOrders}
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                selectedOrders={selectedOrders}
                setSelectedOrders={setSelectedOrders}
                handleSyncOrders={handleSyncOrders}
                standAloneSyncOrders = {false}
                standAloneTrackOrders = {false}
                refreshOrders={fetchHeaderData}
                handleTrackOrders={handleTrackOrders} // Add this prop
                refreshData={refreshOrderData} // Add this prop
                refreshDashboard={refreshStatusSummary}
            />

            {/* Search and Filters */}
            <OrderFilters
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                statusOptions={statusOptions}
                onApplyFilters={applyFilters}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                // Add syncro filter props
                syncroFilter={syncroFilter}
                setSyncroFilter={setSyncroFilter}
                platformFilter={platformFilter}        // Add platform filter
                setPlatformFilter={setPlatformFilter}  // Add platform filter setter
            />

            {/* Orders Table */}
            <div className="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <div className="text-red-500 mb-2">{error}</div>
                        <button
                            onClick={() => window.location.reload()}
                            className="text-[#174540] hover:underline"
                        >
                            Réessayer
                        </button>
                    </div>
                ) : (
                    <OrderTable
                        orders={currentOrders}
                        toggleDropdown={toggleDropdown}
                        openDropdown={openDropdown}
                        handleUpdateStatus={handleUpdateStatus}
                        handleDeleteOrder={handleDeleteOrder}
                        translateStatus={translateStatus}
                        getStatusColor={getStatusColor}
                        statusOptions={allowedStatusOptions}
                        formatDate={formatDate}
                        getClientFullName={getClientFullName}
                        setOpenDropdown={setOpenDropdown}
                        selectedOrders={selectedOrders}
                        handleOrderSelection={handleOrderSelection}
                        refreshData={refreshOrderData}         // Add this prop
                        refreshDashboard={refreshStatusSummary} // Add this prop
                    />
                )}
            </div>

            {/* Remove the Sync Selection button that was here */}

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                pageSize={pageSize}
                paginate={paginate}
                onPageSizeChange={handlePageSizeChange} // Add this line
            />

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDeleteOrder}
                itemId={orderToDelete}
            />

        </div>
    );
};

export default OrdersPage;